/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  Phone,
  ChevronDown,
  Sparkles,
  Mail,
  Send,
  CheckCircle,
  AlertCircle,
  BookOpen,
  ArrowRight,
} from "lucide-react";
import Button from "../ui/Button";
import logo from "../../assets/logo.png";

const API_BASE = "http://192.168.1.8:5000/api"; // Replace with your actual API base URL

const DemoFormModal = ({ onClose, zapdialColor }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    employees: "",
    message: "",
    preferredDate: "",
    preferredTime: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [formStep, setFormStep] = useState(1);
  const [formTouched, setFormTouched] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const validateField = (name, value) => {
    const trimmedValue = String(value || "").trim();

    switch (name) {
      case "email":
        if (!trimmedValue) return "Email is required";
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)
          ? ""
          : "Invalid email address";

      case "phone":
        if (!trimmedValue) return "Phone is required";
        return /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/.test(
          trimmedValue
        )
          ? ""
          : "Invalid phone number";

      case "name":
        if (!trimmedValue) return "Name is required";
        return trimmedValue.length < 2 ? "Name is too short" : "";

      case "company":
        if (!trimmedValue) return "Company name is required";
        return trimmedValue.length < 2 ? "Company name is too short" : "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formTouched[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }

    if (error) setError("");
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;

    setFormTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    setValidationErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  const validateStepOne = () => {
    const requiredFields = ["name", "company", "email", "phone"];
    const errors = {};
    const touched = {};

    requiredFields.forEach((field) => {
      touched[field] = true;
      const fieldError = validateField(field, form[field]);
      if (fieldError) errors[field] = fieldError;
    });

    setFormTouched((prev) => ({ ...prev, ...touched }));
    setValidationErrors((prev) => ({ ...prev, ...errors }));

    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (!validateStepOne()) {
      setError("Please fill all required fields correctly.");
      return;
    }

    setError("");
    setFormStep(2);
  };

  const resetForm = () => {
    setForm({
      name: "",
      company: "",
      email: "",
      phone: "",
      employees: "",
      message: "",
      preferredDate: "",
      preferredTime: "",
    });
    setFormTouched({});
    setValidationErrors({});
    setFormStep(1);
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStepOne()) {
      setError("Please fix the errors before submitting.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const payload = {
        name: form.name.trim(),
        company_name: form.company.trim(),
        email: form.email.trim(),
        mobile: form.phone.trim(),
        business_type: "Website Demo Request",
        number_of_employees: form.employees.trim() || null,
        preferred_date: form.preferredDate || null,
        preferred_time: form.preferredTime || null,
        message: form.message?.trim() || "",
        source: "navbar",
        status: "pending",
      };

      const response = await fetch(`${API_BASE}/demo-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to submit demo request");
      }

      setSuccess(true);

      setTimeout(() => {
        resetForm();
        onClose();
      }, 1800);
    } catch (submitError) {
      console.error("Demo request submit error:", submitError);
      setError(submitError.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = [
    "9:00 AM",
    "10:00 AM",
    "11:00 AM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
  ];

  const styles = {
    stepIndicator: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "28px",
    },
    step: (active, completed) => ({
      flex: 1,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: completed ? "#10b981" : active ? zapdialColor : "#94a3b8",
      fontSize: "12px",
      fontWeight: "600",
    }),
    stepNumber: (active, completed) => ({
      width: "30px",
      height: "30px",
      borderRadius: "50%",
      backgroundColor: completed ? "#10b981" : active ? zapdialColor : "#e2e8f0",
      color: completed || active ? "#ffffff" : "#64748b",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      fontWeight: "700",
    }),
    stepLine: {
      flex: 1,
      height: "2px",
      backgroundColor: "#e2e8f0",
      margin: "0 8px",
    },
    formGroup: {
      marginBottom: "18px",
    },
    formLabel: {
      display: "block",
      fontSize: "13px",
      fontWeight: "700",
      color: "#374151",
      marginBottom: "6px",
    },
    formInput: {
      width: "100%",
      padding: "12px 14px",
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      fontSize: "14px",
      outline: "none",
      fontFamily: "inherit",
    },
    formTextarea: {
      width: "100%",
      padding: "12px 14px",
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      fontSize: "14px",
      resize: "vertical",
      fontFamily: "inherit",
      outline: "none",
    },
    timeSlots: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "8px",
      marginTop: "8px",
    },
    timeSlot: (selected) => ({
      padding: "10px 8px",
      textAlign: "center",
      border: `1px solid ${selected ? zapdialColor : "#e2e8f0"}`,
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "13px",
      color: selected ? zapdialColor : "#64748b",
      backgroundColor: selected ? `${zapdialColor}10` : "#ffffff",
    }),
    navButtons: {
      display: "flex",
      gap: "12px",
      marginTop: "24px",
    },
    errorMessage: {
      backgroundColor: "#fef2f2",
      color: "#b91c1c",
      padding: "12px",
      borderRadius: "12px",
      fontSize: "13px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "20px",
    },
    successMessage: {
      textAlign: "center",
      padding: "30px 20px",
    },
    successIcon: {
      width: "70px",
      height: "70px",
      backgroundColor: "#10b981",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 20px",
      color: "#ffffff",
    },
  };

  if (success) {
    return (
      <div style={styles.successMessage}>
        <div style={styles.successIcon}>
          <CheckCircle size={36} />
        </div>
        <h3 style={{ fontSize: "22px", color: "#0f172a", marginBottom: "8px" }}>
          Thank You!
        </h3>
        <p style={{ color: "#64748b", marginBottom: "16px" }}>
          Your demo request has been submitted successfully.
          <br />
          Our team will contact you within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={styles.stepIndicator}>
        <div style={styles.step(formStep === 1, formStep > 1)}>
          <span style={styles.stepNumber(formStep === 1, formStep > 1)}>1</span>
          <span>Basic Info</span>
        </div>

        <div style={styles.stepLine}></div>

        <div style={styles.step(formStep === 2, formStep > 2)}>
          <span style={styles.stepNumber(formStep === 2, formStep > 2)}>2</span>
          <span>Schedule</span>
        </div>
      </div>

      {error && (
        <div style={styles.errorMessage}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {formStep === 1 && (
        <>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#0f172a",
              marginBottom: "4px",
            }}
          >
            Tell us about yourself
          </h3>

          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>
            We'll customize the demo based on your needs.
          </p>

          {[
            { label: "Full Name *", name: "name", type: "text", placeholder: "John Doe" },
            { label: "Company *", name: "company", type: "text", placeholder: "Acme Inc." },
            { label: "Email *", name: "email", type: "email", placeholder: "john@company.com" },
            { label: "Phone *", name: "phone", type: "tel", placeholder: "+91 9876543210" },
            {
              label: "Number of Employees",
              name: "employees",
              type: "text",
              placeholder: "e.g., 10-50",
            },
          ].map((field) => (
            <div key={field.name} style={styles.formGroup}>
              <label style={styles.formLabel}>{field.label}</label>
              <input
                type={field.type}
                name={field.name}
                value={form[field.name]}
                onChange={handleChange}
                onBlur={handleFieldBlur}
                placeholder={field.placeholder}
                style={styles.formInput}
                onFocus={(e) => (e.target.style.borderColor = zapdialColor)}
              />
              {formTouched[field.name] && validationErrors[field.name] && (
                <p style={{ fontSize: "11px", color: "#ef4444", marginTop: "4px" }}>
                  {validationErrors[field.name]}
                </p>
              )}
            </div>
          ))}

          <div style={styles.navButtons}>
            <button
              type="button"
              onClick={nextStep}
              style={{
                flex: 1,
                padding: "12px",
                backgroundColor: zapdialColor,
                color: "#ffffff",
                border: "none",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "700",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              Next Step <ArrowRight size={16} />
            </button>
          </div>
        </>
      )}

      {formStep === 2 && (
        <>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#0f172a",
              marginBottom: "4px",
            }}
          >
            Schedule your demo
          </h3>

          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>
            Choose a convenient time for your personalized demo.
          </p>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Preferred Date</label>
            <input
              type="date"
              name="preferredDate"
              value={form.preferredDate}
              onChange={handleChange}
              style={styles.formInput}
              onFocus={(e) => (e.target.style.borderColor = zapdialColor)}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Preferred Time</label>
            <div style={styles.timeSlots}>
              {timeSlots.map((slot) => (
                <div
                  key={slot}
                  style={styles.timeSlot(form.preferredTime === slot)}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      preferredTime: slot,
                    }))
                  }
                >
                  {slot}
                </div>
              ))}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Additional Message</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Any specific requirements or questions?"
              rows="3"
              style={styles.formTextarea}
              onFocus={(e) => (e.target.style.borderColor = zapdialColor)}
            />
          </div>

          <div style={styles.navButtons}>
            <button
              type="button"
              onClick={() => setFormStep(1)}
              style={{
                flex: 1,
                padding: "12px",
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Back
            </button>

            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 2,
                padding: "12px",
                backgroundColor: zapdialColor,
                color: "#ffffff",
                border: "none",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "700",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {loading ? (
                "Submitting..."
              ) : (
                <>
                  <Send size={16} /> Request Demo
                </>
              )}
            </button>
          </div>
        </>
      )}
    </form>
  );
};

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const location = useLocation();

  const zapdialColor = "#4f46e5";

  const navLinks = [
    {
      path: "/",
      label: "Home",
      icon: <Sparkles size={16} />,
    },
    {
      label: "Products",
      icon: <Sparkles size={16} />,
      mega: true,
      dropdown: [
        {
          path: "/features",
          label: "Features",
          icon: "✨",
          description:
            "Explore dialer, CRM, live chat, analytics, automation, attendance, and agent tools.",
        },
        {
          path: "/solutions",
          label: "Solutions",
          icon: "💡",
          description:
            "Industry-ready workflows for call centers, real estate, finance, education, and sales teams.",
        },
        {
          path: "/integrations",
          label: "Integrations",
          icon: "🔌",
          description:
            "Connect Facebook, Instagram, Google, WhatsApp, CRM, payments, and marketing tools.",
        },
      ],
    },
    {
      path: "/pricing",
      label: "Pricing",
      icon: <span style={{ fontFamily: "inherit" }}>₹</span>, // Rupee sign instead of DollarSign
    },
    {
      path: "/contact",
      label: "Contact",
      icon: <Mail size={16} />,
    },
  ];

  const isActive = (path) => location.pathname === path;

  const isDropdownActive = (dropdown) => {
    return dropdown?.some((item) => location.pathname === item.path);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10 || location.pathname !== "/");
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) setIsMobileMenuOpen(false);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isDemoModalOpen || isMobileMenuOpen ? "hidden" : "unset";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isDemoModalOpen, isMobileMenuOpen]);

  const handleNavItemHover = (e, activePath) => {
    if (!activePath) {
      e.currentTarget.style.backgroundColor = isScrolled
        ? "rgba(79, 70, 229, 0.08)"
        : "rgba(255, 255, 255, 0.1)";
    }
    e.currentTarget.style.transform = "translateY(-2px)";
  };

  const handleNavItemLeave = (e, activePath) => {
    if (!activePath) e.currentTarget.style.backgroundColor = "transparent";
    e.currentTarget.style.transform = "translateY(0)";
  };

  const styles = {
    navbar: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      backgroundColor: isScrolled ? "rgba(255, 255, 255, 0.98)" : "transparent",
      backdropFilter: isScrolled ? "blur(14px)" : "none",
      boxShadow: isScrolled ? "0 8px 30px rgba(15, 23, 42, 0.08)" : "none",
      transition: "all 0.3s ease",
      padding: isScrolled ? "12px 0" : "20px 0",
    },
    container: {
      maxWidth: "1400px",
      margin: "0 auto",
      padding: "0 40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    logoContainer: {
      display: "flex",
      alignItems: "center",
      textDecoration: "none",
      zIndex: 1100,
    },
    logo: {
      height: isScrolled ? "45px" : "55px",
      width: "auto",
      transition: "all 0.3s ease",
      objectFit: "contain",
      // Logo color unchanged - keep original colors
    },
    navLinks: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      margin: 0,
      padding: 0,
      listStyle: "none",
    },
    navItem: {
      position: "relative",
    },
    navLink: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 20px",
      borderRadius: "999px",
      textDecoration: "none",
      fontSize: "15px",
      fontWeight: "600",
      transition: "all 0.2s ease",
      cursor: "pointer",
      background: "transparent",
      border: "none",
      letterSpacing: "0.2px",
    },
    dropdownButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 20px",
      borderRadius: "999px",
      background: "transparent",
      border: "none",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      letterSpacing: "0.2px",
    },
    dropdownMenu: {
      position: "absolute",
      top: "calc(100% + 16px)",
      left: "50%",
      transform: "translateX(-50%) translateY(10px)",
      backgroundColor: "#ffffff",
      borderRadius: "26px",
      boxShadow: "0 35px 90px rgba(15, 23, 42, 0.2)",
      padding: "18px",
      width: "680px",
      opacity: 0,
      visibility: "hidden",
      transition: "all 0.25s ease",
      zIndex: 1001,
      pointerEvents: "none",
      border: "1px solid #eef2f7",
    },
    dropdownMenuActive: {
      opacity: 1,
      visibility: "visible",
      pointerEvents: "auto",
      transform: "translateX(-50%) translateY(0)",
    },
    megaGrid: {
      display: "grid",
      gridTemplateColumns: "1.2fr 0.8fr",
      gap: "16px",
    },
    dropdownItemList: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    dropdownItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: "14px",
      padding: "16px",
      color: "#334155",
      textDecoration: "none",
      fontSize: "14px",
      borderRadius: "18px",
      transition: "all 0.2s ease",
    },
    megaFeatureBox: {
      background: "linear-gradient(135deg, #eef2ff 0%, #e0f2fe 100%)",
      borderRadius: "22px",
      padding: "22px",
      border: "1px solid #dbeafe",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      minHeight: "250px",
    },
    mobileMenuButton: {
      display: "none",
      background: "none",
      border: "none",
      cursor: "pointer",
      color: isScrolled ? "#1f2937" : "#ffffff",
      padding: "10px",
      borderRadius: "50%",
      transition: "background-color 0.2s ease",
      zIndex: 1100,
    },
    mobileMenu: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "#ffffff",
      padding: "100px 24px 24px",
      transform: isMobileMenuOpen ? "translateX(0)" : "translateX(100%)",
      transition: "transform 0.3s ease-in-out",
      zIndex: 1000,
      overflowY: "auto",
      visibility: isMobileMenuOpen ? "visible" : "hidden",
    },
    mobileNavItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "16px 0",
      color: "#1f2937",
      textDecoration: "none",
      fontSize: "18px",
      fontWeight: "600",
      borderBottom: "1px solid #f3f4f6",
    },
    mobileDropdownTrigger: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      background: "none",
      border: "none",
      padding: "16px 0",
      fontSize: "18px",
      fontWeight: "600",
      color: "#1f2937",
      borderBottom: "1px solid #f3f4f6",
      cursor: "pointer",
    },
    mobileDropdownContent: {
      padding: "8px",
      backgroundColor: "#f8fafc",
      borderRadius: "18px",
      marginBottom: "8px",
      marginTop: "8px",
    },
    mobileDropdownItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      padding: "14px",
      color: "#475569",
      textDecoration: "none",
      fontSize: "15px",
      borderRadius: "14px",
    },
    demoButton: {
      marginLeft: "8px",
    },
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.75)",
      backdropFilter: "blur(5px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
    },
    modalContent: {
      backgroundColor: "#ffffff",
      borderRadius: "28px",
      maxWidth: "550px",
      width: "90%",
      maxHeight: "90vh",
      overflowY: "auto",
      position: "relative",
    },
    modalHeader: {
      padding: "24px 24px 16px",
      borderBottom: "1px solid #e5e7eb",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      position: "sticky",
      top: 0,
      backgroundColor: "#ffffff",
      zIndex: 10,
    },
    modalTitle: {
      fontSize: "24px",
      fontWeight: "800",
      color: "#0f172a",
      margin: 0,
    },
    modalClose: {
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "#9ca3af",
      padding: "8px",
      borderRadius: "10px",
    },
    modalBody: {
      padding: "24px",
    },
  };

  return (
    <>
      <nav style={styles.navbar}>
        <div style={styles.container}>
          <Link to="/" style={styles.logoContainer}>
            <img src={logo} alt="ZapDial Logo" style={styles.logo} />
          </Link>

          <ul style={styles.navLinks} className="desktop-nav">
            {navLinks.map((link, index) => (
              <li
                key={index}
                style={styles.navItem}
                onMouseEnter={() => link.dropdown && setActiveDropdown(index)}
                onMouseLeave={() => link.dropdown && setActiveDropdown(null)}
              >
                {link.dropdown ? (
                  <>
                    <button
                      style={{
                        ...styles.dropdownButton,
                        color:
                          activeDropdown === index || isDropdownActive(link.dropdown)
                            ? zapdialColor
                            : isScrolled
                            ? "#1f2937"
                            : "#ffffff",
                        backgroundColor:
                          activeDropdown === index || isDropdownActive(link.dropdown)
                            ? isScrolled
                              ? "rgba(79, 70, 229, 0.1)"
                              : "rgba(255, 255, 255, 0.15)"
                            : "transparent",
                      }}
                      className="nav-dropdown-btn"
                      type="button"
                    >
                      {link.icon}
                      {link.label}
                      <ChevronDown
                        size={14}
                        style={{
                          transform: activeDropdown === index ? "rotate(180deg)" : "none",
                          transition: "transform 0.2s ease",
                          opacity: 0.75,
                        }}
                      />
                    </button>

                    <div
                      style={{
                        ...styles.dropdownMenu,
                        ...(activeDropdown === index ? styles.dropdownMenuActive : {}),
                      }}
                    >
                      <div style={styles.megaGrid}>
                        <div style={styles.dropdownItemList}>
                          {link.dropdown.map((dropdownItem, idx) => (
                            <Link
                              key={idx}
                              to={dropdownItem.path}
                              style={styles.dropdownItem}
                              className="mega-dropdown-item"
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f8fafc";
                                e.currentTarget.style.transform = "translateY(-2px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.transform = "translateY(0)";
                              }}
                            >
                              <span
                                style={{
                                  width: "46px",
                                  height: "46px",
                                  borderRadius: "15px",
                                  background: "linear-gradient(135deg, #eef2ff, #e0f2fe)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "22px",
                                  flexShrink: 0,
                                }}
                              >
                                {dropdownItem.icon}
                              </span>

                              <span>
                                <strong
                                  style={{
                                    display: "block",
                                    color: "#0f172a",
                                    fontSize: "15px",
                                    marginBottom: "5px",
                                  }}
                                >
                                  {dropdownItem.label}
                                </strong>

                                <span
                                  style={{
                                    display: "block",
                                    color: "#64748b",
                                    fontSize: "13px",
                                    lineHeight: "1.45",
                                  }}
                                >
                                  {dropdownItem.description}
                                </span>
                              </span>
                            </Link>
                          ))}
                        </div>

                        <div style={styles.megaFeatureBox}>
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: zapdialColor,
                                fontWeight: "900",
                                textTransform: "uppercase",
                                letterSpacing: "0.6px",
                                marginBottom: "10px",
                              }}
                            >
                              Featured Product
                            </div>

                            <h3
                              style={{
                                fontSize: "22px",
                                color: "#0f172a",
                                marginBottom: "10px",
                                fontWeight: "900",
                                lineHeight: 1.2,
                              }}
                            >
                              ZapDial Control Center
                            </h3>

                            <p
                              style={{
                                color: "#475569",
                                fontSize: "13px",
                                lineHeight: 1.6,
                                marginBottom: "16px",
                              }}
                            >
                              Manage clients, agents, campaigns, live chat, CRM,
                              reports, integrations, and billing from one
                              enterprise-ready SaaS platform.
                            </p>
                          </div>

                          <Link
                            to="/features"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px",
                              color: "#ffffff",
                              background: zapdialColor,
                              textDecoration: "none",
                              padding: "11px 14px",
                              borderRadius: "14px",
                              fontWeight: "800",
                              fontSize: "13px",
                              width: "fit-content",
                            }}
                          >
                            Explore Features <ArrowRight size={15} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={link.path}
                    style={{
                      ...styles.navLink,
                      color: isActive(link.path)
                        ? zapdialColor
                        : isScrolled
                        ? "#1f2937"
                        : "#ffffff",
                      backgroundColor: isActive(link.path)
                        ? isScrolled
                          ? "rgba(79, 70, 229, 0.1)"
                          : "rgba(255, 255, 255, 0.15)"
                        : "transparent",
                    }}
                    onMouseEnter={(e) => handleNavItemHover(e, isActive(link.path))}
                    onMouseLeave={(e) => handleNavItemLeave(e, isActive(link.path))}
                    className="nav-link"
                  >
                    {link.icon}
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          <div style={styles.demoButton} className="desktop-demo-btn">
            <Button
              variant={isScrolled ? "primary" : "outline-light"}
              size="md"
              icon={<Phone size={18} />}
              onClick={() => setIsDemoModalOpen(true)}
              className="demo-button"
            >
              Request Demo
            </Button>
          </div>

          <button
            style={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="mobile-menu-btn"
            type="button"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <div style={styles.mobileMenu}>
          <div style={{ marginBottom: "32px", textAlign: "center" }}>
            <img src={logo} alt="ZapDial Logo" style={{ height: "50px", width: "auto" }} />
          </div>

          {navLinks.map((link, index) => (
            <div key={index}>
              {link.dropdown ? (
                <>
                  <button
                    style={{
                      ...styles.mobileDropdownTrigger,
                      color:
                        activeDropdown === index || isDropdownActive(link.dropdown)
                          ? zapdialColor
                          : "#1f2937",
                    }}
                    onClick={() =>
                      setActiveDropdown(activeDropdown === index ? null : index)
                    }
                    type="button"
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      {link.icon}
                      {link.label}
                    </span>

                    <ChevronDown
                      size={18}
                      style={{
                        transform: activeDropdown === index ? "rotate(180deg)" : "none",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </button>

                  {activeDropdown === index && (
                    <div style={styles.mobileDropdownContent}>
                      {link.dropdown.map((dropdownItem, idx) => (
                        <Link
                          key={idx}
                          to={dropdownItem.path}
                          style={{
                            ...styles.mobileDropdownItem,
                            color: isActive(dropdownItem.path)
                              ? zapdialColor
                              : "#475569",
                          }}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span style={{ fontSize: "22px", flexShrink: 0 }}>
                            {dropdownItem.icon}
                          </span>

                          <span>
                            <strong style={{ display: "block", marginBottom: "4px" }}>
                              {dropdownItem.label}
                            </strong>
                            <span
                              style={{
                                display: "block",
                                fontSize: "12px",
                                color: "#64748b",
                                lineHeight: 1.45,
                              }}
                            >
                              {dropdownItem.description}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={link.path}
                  style={{
                    ...styles.mobileNavItem,
                    color: isActive(link.path) ? zapdialColor : "#1f2937",
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.icon}
                  {link.label}
                </Link>
              )}
            </div>
          ))}

          <div style={{ marginTop: "32px" }}>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              icon={<Phone size={18} />}
              onClick={() => {
                setIsDemoModalOpen(true);
                setIsMobileMenuOpen(false);
              }}
            >
              Request Demo
            </Button>
          </div>

          <div style={{ marginTop: "32px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>
            <p>© 2024 ZapDial. All rights reserved.</p>
          </div>
        </div>
      </nav>

      {isDemoModalOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsDemoModalOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>📅 Request a Demo</h3>

              <button
                style={styles.modalClose}
                onClick={() => setIsDemoModalOpen(false)}
                type="button"
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <DemoFormModal
                onClose={() => setIsDemoModalOpen(false)}
                zapdialColor={zapdialColor}
              />
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 1024px) {
          .desktop-nav {
            display: none !important;
          }

          .desktop-demo-btn {
            display: none !important;
          }

          .mobile-menu-btn {
            display: block !important;
          }
        }

        @media (min-width: 1025px) {
          .mobile-menu-btn {
            display: none !important;
          }
        }

        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-3px); }
          100% { transform: translateY(0px); }
        }

        .demo-button {
          animation: float 3s ease-in-out infinite;
        }

        .demo-button:hover {
          animation: none;
          transform: translateY(-2px);
        }

        .nav-link,
        .nav-dropdown-btn,
        .mobile-menu-btn,
        .mega-dropdown-item {
          transition: all 0.2s ease !important;
        }

        @media (max-width: 1180px) {
          nav ul.desktop-nav {
            gap: 4px !important;
          }
        }
      `}</style>
    </>
  );
}